import { toast } from "react-toastify";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import logger from "../helpers/logger";
import { appInsights } from "../appInsights";
import tokenService from "./token-service";
import roleService from "./role-service";

/**
 * Proactive Authentication Monitor Service
 * Handles token expiration warnings, automatic refresh, and user notifications
 * 
 * MFA/Passkey Considerations:
 * - With MFA enabled, InteractionRequiredAuthError may occur more frequently
 * - Azure AD may require interactive re-authentication more often due to Conditional Access policies
 * - Silent token refresh may fail when MFA session expires in Azure AD
 * - These scenarios are handled gracefully with warning-level logging (not errors)
 */
class AuthMonitorService {
  constructor() {
    this.msalInstance = null;
    this.currentAccount = null;
    this.refreshInterval = null;
    this.warningInterval = null;
    this.isRefreshing = false;
    this.lastWarningTime = null;
    this.listeners = new Set();
    this.lastNotifiedStatus = null; // Track last status to prevent unnecessary updates

    // Configuration
    this.WARNING_THRESHOLD_MINUTES = 10; // Warn when 10 minutes left
    this.REFRESH_INTERVAL_MINUTES = 10; // Check every 10 minutes (reduced from 5)
    this.AUTO_REFRESH_THRESHOLD_MINUTES = 15; // Auto-refresh when 15 minutes left
    this.CRITICAL_THRESHOLD_MINUTES = 2; // Critical warning when 2 minutes left
  }

  /**
   * Get timestamp prefix for logging (dev/test environments only)
   */
  getLogTimestamp() {
    const environment = import.meta.env.REACT_APP_ENVIRONMENT || "Dev";
    const isDevelopment = environment === "Dev" || environment === "Test";
    return isDevelopment ? `[${new Date().toLocaleTimeString()}] ` : "";
  }

  /**
   * Initialize the authentication monitor
   */
  initialize(msalInstance, account) {
    // Prevent multiple initializations
    if (this.refreshInterval) {
      logger.log("⚠️ Auth monitor already initialized, skipping");
      return;
    }

    logger.log("🔐 Initializing Authentication Monitor Service");

    this.msalInstance = msalInstance;
    this.currentAccount = account;

    // Start monitoring
    this.startMonitoring();

    // Track initialization
    appInsights.trackEvent({
      name: "AuthMonitor_Initialized",
      properties: {
        user: account?.username || "unknown",
        timestamp: new Date().toISOString(),
        warningThreshold: this.WARNING_THRESHOLD_MINUTES,
        autoRefreshThreshold: this.AUTO_REFRESH_THRESHOLD_MINUTES,
      },
    });
  }

  /**
   * Start the monitoring intervals
   */
  startMonitoring() {
    // Clear any existing intervals
    this.stopMonitoring();

    // Set up periodic token checking
    this.refreshInterval = setInterval(() => {
      this.checkTokenStatus();
    }, this.REFRESH_INTERVAL_MINUTES * 60 * 1000);

    // Initial check
    this.checkTokenStatus();

    logger.log(
      `✅ Authentication monitoring started (checking every ${this.REFRESH_INTERVAL_MINUTES} minutes)`
    );
  }

  /**
   * Stop monitoring (cleanup)
   */
  stopMonitoring() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.warningInterval) {
      clearInterval(this.warningInterval);
      this.warningInterval = null;
    }
    logger.log("🛑 Authentication monitoring stopped");
  }

  /**
   * Check current token status and take appropriate actions
   */
  async checkTokenStatus() {
    if (!this.msalInstance || !this.currentAccount) {
      logger.warn("⚠️ AuthMonitor: No MSAL instance or account available");
      return;
    }

    try {
      // Get current token information
      const tokenInfo = await this.getTokenInfo();

      if (!tokenInfo) {
        logger.warn("⚠️ AuthMonitor: Could not retrieve token information");
        this.notifyListeners({
          status: "unknown",
          message: "Token status unknown",
        });
        return;
      }

      const { expiresIn, isExpired, isNearExpiry, isCritical } = tokenInfo;

      logger.log(`${this.getLogTimestamp()}🔍 Token Status: ${Math.round(expiresIn)} minutes remaining`);

      // Handle different scenarios
      if (isExpired) {
        await this.handleExpiredToken();
      } else if (isCritical) {
        await this.handleCriticalExpiry(expiresIn);
      } else if (isNearExpiry) {
        await this.handleNearExpiry(expiresIn);
      } else if (expiresIn <= this.AUTO_REFRESH_THRESHOLD_MINUTES) {
        await this.handleAutoRefresh(expiresIn);
      } else {
        // Token is healthy
        this.notifyListeners({
          status: "healthy",
          expiresIn,
          message: `Authentication valid for ${Math.round(expiresIn)} minutes`,
        });
      }
    } catch (error) {
      logger.error("❌ AuthMonitor: Error checking token status", error);
      this.notifyListeners({
        status: "error",
        message: "Authentication check failed",
        error: error.message,
      });
    }
  }

  /**
   * Get detailed token information
   */
  async getTokenInfo() {
    try {
      // Verify account still exists before attempting refresh
      const accounts = this.msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        logger.warn("⚠️ AuthMonitor: No accounts found in MSAL cache");
        return null;
      }

      // Update currentAccount if it's stale
      const activeAccount = accounts.find(
        (acc) => acc.homeAccountId === this.currentAccount?.homeAccountId
      ) || accounts[0];

      if (activeAccount.homeAccountId !== this.currentAccount?.homeAccountId) {
        logger.log("🔄 AuthMonitor: Account reference updated");
        this.currentAccount = activeAccount;
      }

      // Try to get a fresh token silently (this also gives us expiration info)
      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: ["User.Read", "Mail.Send"],
        account: this.currentAccount,
      });

      if (!tokenResponse || !tokenResponse.expiresOn) {
        return null;
      }

      const now = new Date();
      const expiresOn = new Date(tokenResponse.expiresOn);
      const expiresInMs = expiresOn.getTime() - now.getTime();
      const expiresInMinutes = expiresInMs / (1000 * 60);

      return {
        expiresIn: expiresInMinutes,
        isExpired: expiresInMinutes <= 0,
        isNearExpiry: expiresInMinutes <= this.WARNING_THRESHOLD_MINUTES,
        isCritical: expiresInMinutes <= this.CRITICAL_THRESHOLD_MINUTES,
        tokenResponse,
      };
    } catch (error) {
      // Handle InteractionRequiredAuthError - session doesn't exist in Azure AD
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn("⚠️ AuthMonitor: Session expired (interaction required)", {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
        });

        // Don't spam logs - this is expected when session expires
        // The session will be handled when user tries to use the app
        return null;
      }

      logger.error("❌ AuthMonitor: Failed to get token info", error);
      return null;
    }
  }

  /**
   * Handle expired token
   */
  async handleExpiredToken() {
    logger.warn("🚨 Token has expired!");

    appInsights.trackEvent({
      name: "AuthMonitor_TokenExpired",
      properties: {
        user: this.currentAccount?.username || "unknown",
        timestamp: new Date().toISOString(),
      },
    });

    toast.error(
      "Your session has expired. Please refresh the page to log in again.",
      {
        autoClose: false,
        toastId: "auth-expired",
      }
    );

    this.notifyListeners({
      status: "expired",
      message: "Session expired - refresh required",
      requiresAction: true,
    });
  }

  /**
   * Handle critical expiry (< 2 minutes)
   */
  async handleCriticalExpiry(expiresIn) {
    logger.warn(
      `🚨 CRITICAL: Token expires in ${Math.round(expiresIn)} minutes!`
    );

    appInsights.trackEvent({
      name: "AuthMonitor_CriticalExpiry",
      properties: {
        user: this.currentAccount?.username || "unknown",
        expiresInMinutes: expiresIn,
        timestamp: new Date().toISOString(),
      },
    });

    // Attempt emergency refresh
    const refreshSuccess = await this.attemptTokenRefresh();

    if (!refreshSuccess) {
      toast.error(
        `🚨 URGENT: Session expires in ${Math.round(
          expiresIn
        )} minutes! Save your work and refresh the page.`,
        {
          autoClose: false,
          toastId: "auth-critical",
        }
      );

      this.notifyListeners({
        status: "critical",
        expiresIn,
        message: `URGENT: Session expires in ${Math.round(expiresIn)} minutes`,
        requiresAction: true,
      });
    }
  }

  /**
   * Handle near expiry warning
   */
  async handleNearExpiry(expiresIn) {
    // Avoid spamming warnings
    const now = Date.now();
    if (this.lastWarningTime && now - this.lastWarningTime < 5 * 60 * 1000) {
      // 5 minutes
      return;
    }

    logger.warn(`⚠️ Token expires in ${Math.round(expiresIn)} minutes`);
    this.lastWarningTime = now;

    appInsights.trackEvent({
      name: "AuthMonitor_NearExpiry",
      properties: {
        user: this.currentAccount?.username || "unknown",
        expiresInMinutes: expiresIn,
        timestamp: new Date().toISOString(),
      },
    });

    toast.warning(
      `Your session expires in ${Math.round(
        expiresIn
      )} minutes. Consider saving your work.`,
      {
        autoClose: 8000,
        toastId: "auth-warning",
      }
    );

    this.notifyListeners({
      status: "warning",
      expiresIn,
      message: `Session expires in ${Math.round(expiresIn)} minutes`,
    });
  }

  /**
   * Handle automatic token refresh
   */
  async handleAutoRefresh(expiresIn) {
    logger.log(
      `${this.getLogTimestamp()}🔄 Auto-refreshing token (${Math.round(expiresIn)} minutes remaining)`
    );

    const refreshSuccess = await this.attemptTokenRefresh();

    if (refreshSuccess) {
      toast.success("Session automatically renewed", {
        autoClose: 3000,
        toastId: "auth-renewed",
      });

      this.notifyListeners({
        status: "renewed",
        message: "Session automatically renewed",
      });
    }
  }

  /**
   * Attempt to refresh the token
   */
  async attemptTokenRefresh() {
    if (this.isRefreshing) {
      logger.log(`${this.getLogTimestamp()}🔄 Token refresh already in progress`);
      return false;
    }

    this.isRefreshing = true;

    try {
      logger.log(`${this.getLogTimestamp()}🔄 Attempting token refresh...`);

      // Verify account still exists before attempting refresh
      const accounts = this.msalInstance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        logger.warn("⚠️ AuthMonitor: No accounts found for token refresh");
        return false;
      }

      // Update currentAccount if it's stale
      const activeAccount = accounts.find(
        (acc) => acc.homeAccountId === this.currentAccount?.homeAccountId
      ) || accounts[0];

      if (activeAccount.homeAccountId !== this.currentAccount?.homeAccountId) {
        logger.log("🔄 AuthMonitor: Account reference updated during refresh");
        this.currentAccount = activeAccount;
      }

      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: ["User.Read", "Mail.Send"],
        account: this.currentAccount,
        forceRefresh: true, // Force refresh
      });

      if (tokenResponse && tokenResponse.accessToken) {
        // Update token service with new token
        tokenService.setToken(tokenResponse.accessToken);
        logger.log("✅ Token refresh successful and updated in token service");

        roleService.clearCache();

        appInsights.trackEvent({
          name: "AuthMonitor_RefreshSuccess",
          properties: {
            user: this.currentAccount?.username || "unknown",
            timestamp: new Date().toISOString(),
          },
        });

        return true;
      } else {
        throw new Error("No access token in response");
      }
    } catch (error) {
      // Handle InteractionRequiredAuthError - session doesn't exist in Azure AD
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn("⚠️ AuthMonitor: Session expired - interaction required for refresh", {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
        });

        appInsights.trackEvent({
          name: "AuthMonitor_RefreshFailed_InteractionRequired",
          properties: {
            user: this.currentAccount?.username || "unknown",
            errorCode: error.errorCode,
            timestamp: new Date().toISOString(),
          },
        });

        // Don't log as error - this is expected when session expires
        return false;
      }

      logger.error("❌ Token refresh failed", error);

      appInsights.trackEvent({
        name: "AuthMonitor_RefreshFailed",
        properties: {
          user: this.currentAccount?.username || "unknown",
          error: error.message,
          timestamp: new Date().toISOString(),
        },
      });

      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Add listener for authentication status changes
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of status changes (only if status actually changed)
   */
  notifyListeners(status) {
    // Compare with last notified status to prevent unnecessary updates
    // Round to 5-minute intervals to reduce flashing from minor time changes
    const roundedMinutes = Math.ceil((status.expiresIn || 0) / 5) * 5;
    const statusKey = `${status.status}-${roundedMinutes}`;
    const lastStatusKey = this.lastNotifiedStatus
      ? `${this.lastNotifiedStatus.status}-${Math.ceil((this.lastNotifiedStatus.expiresIn || 0) / 5) * 5
      }`
      : null;

    // Only notify if status actually changed (or it's the first notification)
    if (statusKey !== lastStatusKey) {
      logger.log(
        `${this.getLogTimestamp()}🔔 Auth status changed: ${status.status} (~${roundedMinutes} min remaining)`
      );
      this.lastNotifiedStatus = { ...status };

      this.listeners.forEach((callback) => {
        try {
          callback(status);
        } catch (error) {
          logger.error("❌ AuthMonitor: Error in listener callback", error);
        }
      });
    } else {
      // Status hasn't meaningfully changed, skip notification
      logger.log(
        `${this.getLogTimestamp()}⏭️ Auth status unchanged: ${status.status} (~${roundedMinutes} min remaining)`
      );
    }
  }

  /**
   * Manual refresh trigger
   */
  async manualRefresh() {
    logger.log("🔄 Manual token refresh requested");
    return await this.attemptTokenRefresh();
  }

  /**
   * Get current authentication status
   */
  async getCurrentStatus() {
    const tokenInfo = await this.getTokenInfo();
    if (!tokenInfo) {
      return { status: "unknown", message: "Unable to determine status" };
    }

    const { expiresIn, isExpired, isNearExpiry, isCritical } = tokenInfo;

    if (isExpired)
      return { status: "expired", expiresIn, message: "Session expired" };
    if (isCritical)
      return {
        status: "critical",
        expiresIn,
        message: `${Math.round(expiresIn)} minutes remaining`,
      };
    if (isNearExpiry)
      return {
        status: "warning",
        expiresIn,
        message: `${Math.round(expiresIn)} minutes remaining`,
      };

    return {
      status: "healthy",
      expiresIn,
      message: `${Math.round(expiresIn)} minutes remaining`,
    };
  }
}

// Create singleton instance
const authMonitorService = new AuthMonitorService();

export default authMonitorService;
